import React, { useEffect, useMemo, useState } from "react";
import { formatNumber } from "../../../utils/format";
import { Alert, Box, Button, Tooltip, Typography } from "@mui/material";
import { MaterialReactTable } from "material-react-table";
import { useNavigate } from "react-router-dom";
import { FileDownload } from "@mui/icons-material";
import * as XLSX from "xlsx";
import { fitColumns } from "../../../utils/excelFit";
import { apiData } from "../../../services/api_service";
import { productWiseInStockAPI } from "../../../config";
import ReservedStockDetailsModal from "../../../components/ReservedStockDetailsModal";
import { buildExportFileName } from "../../../utils/exportFileName";
import { writeXlsxFrozen } from "../../../utils/xlsxFreeze";

// Product-wise inventory table with stock split across every store. Rendered
// inside StoreInventoryReports when the view toggle is set to "product".
const ProductWiseInventory = ({ alert = null, onClearAlert, storeId = 0 }) => {
  const navigate = useNavigate();
  const [tblList, setTblList] = useState([]);
  const [summary, setSummary] = useState({});
  const [stores, setStores] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 100 });
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [reservedDetail, setReservedDetail] = useState(null); // { product_id, product_name }

  const buildPayload = (pageSize) => ({
    pagination: JSON.stringify({
      pageIndex: pageSize ? 0 : pagination.pageIndex,
      pageSize: pageSize || pagination.pageSize,
    }),
    sorting: sorting.length ? JSON.stringify(sorting) : null,
    search: columnFilters.length ? JSON.stringify(columnFilters) : null,
    // Dashboard Inventory Alerts drill-down (same window/store/creator the
    // dashboard used to count the alert).
    ...(alert
      ? {
          alert_filter: alert.alertFilter,
          from_date: alert.fromDate || undefined,
          to_date: alert.toDate || undefined,
          period: alert.period || undefined,
          store_id: alert.storeId || undefined,
          user_id: alert.userId || undefined,
        }
      : {}),
    ...(Number(storeId) > 0 ? { store_id: storeId } : {}),
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await apiData("get", productWiseInStockAPI, buildPayload());
      if (response.success && response.data?.dataRows) {
        setTblList(response.data.dataRows);
        setRowCount(response.data.totalCount || 0);
        setSummary(response.data.summary || {});
        setStores(response.data.stores || []);
      } else {
        setTblList([]);
        setRowCount(0);
        setSummary({});
        setStores([]);
      }
    } catch (e) {
      console.error("Failed to fetch product-wise stock", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnFilters, sorting, pagination, alert, storeId]);

  const handleExport = async () => {
    try {
      setLoading(true);
      const response = await apiData(
        "get",
        productWiseInStockAPI,
        buildPayload(10000000)
      );
      const rows = response?.data?.dataRows || [];
      if (!rows.length) {
        alert("No data to export!");
        return;
      }
      const exportData = rows.map((item, i) => ({
        "#": i + 1,
        "Product Name": item.product_name || "",
        "SKU Code": item.sku || "",
        ...(response?.data?.stores || stores).reduce((acc, store) => {
          acc[store.store_name || `Store ${store.id}`] = Number(
            item.store_breakdown?.[String(store.id)] || 0
          );
          return acc;
        }, {}),
        "Total In Stock": Number(item.instock || 0),
        Reserved: Number(item.reserved || 0),
        Damaged: Number(item.damaged || 0),
        Missing: Number(item.missing || 0),
        Expired: Number(item.expired || 0),
        Available: Number(item.available || 0),
      }));
      // TOTAL row: sum of the values written in the rows above (= on-screen footer)
      const totalRow = { "#": "", "Product Name": "TOTAL" };
      Object.keys(exportData[0]).forEach((k) => {
        if (k === "#" || k === "Product Name" || k === "SKU Code") return;
        totalRow[k] = exportData.reduce((s, r) => s + (Number(r[k]) || 0), 0);
      });
      exportData.push(totalRow);
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      fitColumns(ws);
      XLSX.utils.book_append_sheet(wb, ws, "Product_Wise_Inventory");
      writeXlsxFrozen(XLSX, 
        wb,
        buildExportFileName("Product_Wise_Inventory", [
          { label: "Scope", value: "All_Products_All_Stores" },
        ])
      );
    } catch (e) {
      console.error("Export failed:", e);
      alert("Failed to export data.");
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "product_name",
        header: "Product Name",
        size: 10,
        Cell: ({ row }) => (
          <Tooltip title="View store-wise breakdown" arrow>
            <Button
              variant="text"
              sx={{ textTransform: "none" }}
              onClick={() =>
                navigate(`/reports/product-stock/${row.original.product_id}`, {
                  state: {
                    product_name: row.original.product_name,
                    sku: row.original.sku,
                  },
                })
              }
            >
              {row.original.product_name}
            </Button>
          </Tooltip>
        ),
        Footer: () => <Typography fontWeight={600}>TOTAL</Typography>,
      },
      { accessorKey: "sku", header: "SKU Code", size: 10 },
      ...stores.map((store) => ({
        id: `store_${store.id}`,
        header: store.store_name || `Store ${store.id}`,
        accessorFn: (row) => Number(row.store_breakdown?.[String(store.id)] || 0),
        size: 110,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) => formatNumber(cell.getValue()),
      })),
      {
        accessorKey: "instock",
        header: "Total In Stock",
        size: 10,
        enableColumnFilter: false,
        Cell: ({ row }) => formatNumber(row.original.instock),
        Footer: () => (
          <Typography fontWeight={600}>{formatNumber(summary?.total_quantity ?? 0)}</Typography>
        ),
      },
      {
        accessorKey: "reserved",
        header: "Reserved",
        size: 10,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const r = Number(row.original.reserved || 0);
          if (r <= 0) {
            return (
              <Typography fontSize="inherit" sx={{ fontWeight: 400 }}>
                {formatNumber(r)}
              </Typography>
            );
          }
          return (
            <Tooltip title="Click to see which CDN(s)/salesman are holding this" arrow>
              <Typography
                fontSize="inherit"
                onClick={() =>
                  setReservedDetail({
                    product_id: row.original.product_id,
                    product_name: row.original.product_name,
                  })
                }
                sx={{
                  fontWeight: 600,
                  color: "#c62828",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                {formatNumber(r)}
              </Typography>
            </Tooltip>
          );
        },
        Footer: () => (
          <Typography fontWeight={600}>{formatNumber(summary?.total_reserved ?? 0)}</Typography>
        ),
      },
      {
        accessorKey: "damaged",
        header: "Damaged",
        size: 10,
        enableColumnFilter: false,
        Cell: ({ row }) => formatNumber(row.original.damaged),
        Footer: () => (
          <Typography fontWeight={600}>{formatNumber(summary?.total_damaged ?? 0)}</Typography>
        ),
      },
      {
        accessorKey: "missing",
        header: "Missing",
        size: 10,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const m = Number(row.original.missing || 0);
          if (m <= 0) {
            return (
              <Typography fontSize="inherit" sx={{ fontWeight: 400 }}>
                {formatNumber(m)}
              </Typography>
            );
          }
          return (
            <Tooltip title="Flagged missing at a store — see Missing Stock" arrow>
              <Typography
                fontSize="inherit"
                sx={{ fontWeight: 600, color: "#e65100" }}
              >
                {formatNumber(m)}
              </Typography>
            </Tooltip>
          );
        },
        Footer: () => (
          <Typography fontWeight={600}>{formatNumber(summary?.total_missing ?? 0)}</Typography>
        ),
      },
      {
        accessorKey: "expired",
        header: "Expired",
        size: 10,
        enableColumnFilter: false,
        // In "Total In Stock" but past the expiry date, so not sellable and
        // left out of Available.
        Cell: ({ row }) => {
          const e = Number(row.original.expired || 0);
          return (
            <Tooltip title={e > 0 ? "Past expiry date — in stock but not sellable" : ""} arrow>
              <Typography fontSize="inherit" sx={{ fontWeight: e > 0 ? 600 : 400, color: e > 0 ? "#be123c" : "inherit" }}>
                {formatNumber(e)}
              </Typography>
            </Tooltip>
          );
        },
        Footer: () => (
          <Typography fontWeight={600} sx={{ color: "#be123c" }}>{formatNumber(summary?.total_expired ?? 0)}</Typography>
        ),
      },
      {
        accessorKey: "available",
        header: "Available",
        size: 10,
        enableColumnFilter: false,
        Cell: ({ row }) => formatNumber(row.original.available),
        Footer: () => (
          <Typography fontWeight={600}>{formatNumber(summary?.total_available ?? 0)}</Typography>
        ),
      },
    ],
    [summary, navigate, stores]
  );

  return (
    <>
    {alert && (
      <Alert
        severity="info"
        sx={{ mb: 1.5 }}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={() => {
              setPagination((p) => ({ ...p, pageIndex: 0 }));
              onClearAlert && onClearAlert();
            }}
          >
            Clear filter
          </Button>
        }
      >
        Filtered from the dashboard — {alert.reportTitle || alert.alertFilter} ({rowCount} products).
      </Alert>
    )}
    <MaterialReactTable
      enableGlobalFilter={false}
      enableGlobalFiltering={false}
      columns={columns}
      data={tblList}
      enableTableFooter
      getRowId={(row) => `${row.product_id}`}
      initialState={{
        showColumnFilters: true,
        columnPinning: { left: ["mrt-row-numbers", "product_name", "sku"], right: ["instock"] },
      }}
      enableColumnPinning
      enableRowNumbers
      rowNumberMode="original"
      enableFullScreenToggle={false}
      enableStickyHeader
      manualFiltering
      manualPagination
      manualSorting
      onColumnFiltersChange={setColumnFilters}
      onPaginationChange={setPagination}
      onSortingChange={setSorting}
      rowCount={rowCount}
      state={{ columnFilters, isLoading: loading, pagination, sorting }}
      muiTableFooterProps={{
        sx: {
          position: "sticky",
          bottom: 0,
          zIndex: 100,
          backgroundColor: "#fff",
          boxShadow: "0px -2px 8px rgba(0,0,0,0.1)",
        },
      }}
      muiTableContainerProps={{
        sx: {
          maxHeight: "calc(100vh - 240px)",
          minHeight: 450,
          overflowY: "auto",
        },
      }}
      muiTableBodyCellProps={{
        sx: { padding: "3px 8px", border: "1px solid #ccc" },
      }}
      muiTableHeadCellProps={{
        sx: {
          padding: "8px",
          border: "1px solid #ccc",
          color: "#fff !important",
          fontWeight: "bold",
          backgroundColor: "#1976d2",
          "& svg": { color: "#fff !important" },
          "& .MuiTableSortLabel-root": {
            color: "#fff !important",
            "&.Mui-active": {
              color: "#fff !important",
              "& .MuiTableSortLabel-icon": { color: "#fff !important" },
            },
          },
          "& .MuiTableSortLabel-icon": { color: "#fff !important" },
          "& .MuiInputBase-input": {
            color: "#fff !important",
            textFillColor: "#fff !important",
          },
          "& .MuiInputBase-input::placeholder": {
            color: "#fff !important",
            opacity: 0.8,
          },
          "& .MuiInput-underline:before": {
            borderBottomColor: "#fff !important",
          },
          "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
            borderBottomColor: "#fff !important",
          },
          "& .MuiInput-underline:after": {
            borderBottomColor: "#fff !important",
          },
        },
      }}
      muiTableBodyRowProps={({ row }) => ({
        sx: {
          borderBottom: "1px solid #ccc",
          backgroundColor: row.index % 2 === 0 ? "#ffffff" : "#e3f2fd",
        },
      })}
      renderTopToolbarCustomActions={() => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: "0.5rem" }}>
          <Tooltip title="Export data to excel sheet" arrow>
            <Button
              onClick={handleExport}
              style={{ background: "#1a73e829" }}
              startIcon={<FileDownload />}
            >
              Export To Excel
            </Button>
          </Tooltip>
        </Box>
      )}
    />
    <ReservedStockDetailsModal
      open={!!reservedDetail}
      onClose={() => setReservedDetail(null)}
      productId={reservedDetail?.product_id}
      productName={reservedDetail?.product_name}
    />
    </>
  );
};

export default ProductWiseInventory;
